/**
 * Password validation utilities
 * Shared validation logic for password requirements across the application
 */

/** Validation status: -1 = failed, 0 = passed */
export type ValidationStatus = -1 | 0;

export type PasswordValidationResult = {
	length: ValidationStatus;
	letter: ValidationStatus;
	number: ValidationStatus;
	specialChar: ValidationStatus;
	differentFromUsername: ValidationStatus;
};

export type PasswordRules = {
	readonly MIN_LENGTH: number;
	readonly MAX_LENGTH: number;
};

export type PasswordPatterns = {
	readonly hasLetter: RegExp;
	readonly hasNumber: RegExp;
	readonly hasSpecialChar: RegExp;
};

export const PASSWORD_RULES: PasswordRules = {
	MIN_LENGTH: 15,
	MAX_LENGTH: 63,
} as const;

/**
 * Regex patterns for password validation
 * Note: These patterns do NOT use the global flag to avoid stateful behavior with .test()
 */
export const PASSWORD_PATTERNS: PasswordPatterns = {
	hasLetter: /[a-zA-Z]/,
	hasNumber: /\d/,
	// Special characters include: !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~ and space
	hasSpecialChar: /[!"#$%&'()*\s+,\-./:;<=>?@[\]^_`{|}~]/,
} as const;

/**
 * Validates a password against all requirements
 * @param password - The password to validate
 * @param username - The username to check against (password cannot match username)
 * @returns Object with validation results for each rule (-1 = failed, 0 = passed)
 */
export const validatePassword = (
	password: string,
	username: string = ''
): PasswordValidationResult => {
	const { MIN_LENGTH, MAX_LENGTH } = PASSWORD_RULES;
	const { hasLetter, hasNumber, hasSpecialChar } = PASSWORD_PATTERNS;

	return {
		length:
			password.length >= MIN_LENGTH && password.length <= MAX_LENGTH ? 0 : -1,
		letter: hasLetter.test(password) ? 0 : -1,
		number: hasNumber.test(password) ? 0 : -1,
		specialChar: hasSpecialChar.test(password) ? 0 : -1,
		differentFromUsername: password !== username ? 0 : -1,
	};
};

/**
 * Checks if all password validations pass
 * @param validationResult - Result from validatePassword()
 * @returns True if all validations pass
 */
export const isPasswordValid = (
	validationResult: PasswordValidationResult
): boolean => {
	return Object.values(validationResult).every(value => value === 0);
};

/**
 * Gets a specific error message for the first failing validation
 * @param password - The password to validate
 * @param username - The username to check against
 * @returns Error message or null if valid
 */
export const getPasswordErrorMessage = (
	password: string,
	username: string = ''
): string | null => {
	const { MIN_LENGTH, MAX_LENGTH } = PASSWORD_RULES;
	const { hasLetter, hasNumber, hasSpecialChar } = PASSWORD_PATTERNS;

	if (password.length > MAX_LENGTH) {
		return `Password cannot exceed ${MAX_LENGTH} characters.`;
	}
	if (password.length < MIN_LENGTH) {
		return `Password must be at least ${MIN_LENGTH} characters long.`;
	}
	if (!hasLetter.test(password)) {
		return 'At least 1 letter required.';
	}
	if (!hasNumber.test(password)) {
		return 'At least 1 number required.';
	}
	if (!hasSpecialChar.test(password)) {
		return 'At least 1 special character or space required.';
	}
	if (password === username) {
		return 'Password cannot be the same as your username.';
	}

	return null;
};

export default {
	PASSWORD_RULES,
	PASSWORD_PATTERNS,
	validatePassword,
	isPasswordValid,
	getPasswordErrorMessage,
};
